import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { useNavigate, Link, useLocation } from "react-router-dom";
import AuthService from "../services/auth.service";
import PhoneLoginPanel from "./PhoneLoginPanel";
import "./Auth.css";
import logo from '../assets/logo.png';
import { GoogleLogin } from '@react-oauth/google';
import { Clock, Users, ShieldCheck, ArrowLeft } from 'lucide-react';
import AppStoreBadge from '../assets/a_store.png';
import GooglePlayBadge from '../assets/g_play.png';

const schema = yup.object({
    username: yup.string().required("Zadajte e-mail"),
    password: yup.string().required("Zadajte heslo"),
    rememberMe: yup.boolean(),
}).required();

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [showResend, setShowResend] = useState(false);
    const [resendStatus, setResendStatus] = useState("");
    // Prihlásenie číslom je pre majiteľov, ktorí heslo do admina nemajú.
    const [phoneMode, setPhoneMode] = useState(false);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        if (queryParams.get("verified") === "true") {
            setSuccessMessage("Your email has been successfully verified. You can now log in.");
            navigate('/login', { replace: true }); // Clear query params from URL
        }
    }, [location, navigate]);

    const { register, handleSubmit, getValues, formState: { errors } } = useForm({
        resolver: yupResolver(schema)
    });

    const handleLogin = (data) => {
        setMessage("");
        setLoading(true);
        setShowResend(false);
        setResendStatus("");

        AuthService.login(data.username, data.password, data.rememberMe).then(
            () => {
                navigate("/dashboard");
                window.location.reload();
            },
            (error) => {
                const resMessage =
                    (error.response &&
                        error.response.data &&
                        error.response.data.message) ||
                    error.message ||
                    error.toString();

                if (resMessage.includes("Account not verified")) {
                    setShowResend(true);
                }
                setLoading(false);
                setMessage(resMessage);
            }
        );
    };

    const handleResend = () => {
        setLoading(true);
        const username = getValues("username");
        AuthService.resendVerification(username).then(
            (response) => {
                setLoading(false);
                setShowResend(false);
                setMessage("");
                setResendStatus(response.data.message);
            },
            (error) => {
                const resMessage =
                    (error.response &&
                        error.response.data &&
                        error.response.data.message) ||
                    error.message ||
                    error.toString();
                setLoading(false);
                setResendStatus(resMessage);
            }
        );
    };

    const handleGoogleLoginSuccess = (credentialResponse) => {
        setLoading(true);
        AuthService.googleLogin(credentialResponse.credential).then(
            () => {
                navigate("/dashboard");
                window.location.reload();
            },
            (error) => {
                const resMessage =
                    (error.response &&
                        error.response.data &&
                        error.response.data.message) ||
                    error.message ||
                    error.toString();

                setLoading(false);
                setMessage(resMessage);
            }
        );
    };

    const handleGoogleLoginError = () => {
        setLoading(false);
        console.log('Login Failed');
        setMessage("Google login failed. Please try again.");
    };

    return (
        <div className="auth-container">
            <div className="auth-promo-panel">
                <div className="promo-content">
                    <div className="promo-header">
                        <img src={logo} alt="Effzone Logo" className="promo-logo" />
                        <div className="promo-logo-text">
                            <span className="promo-logo-title">Effzone</span>
                            <span className="promo-logo-subtitle">Dochádzka a komunikácia</span>
                        </div>
                    </div>
                    <h1 className="promo-title">Dochádzka a&nbsp;firemná komunikácia v&nbsp;jednej&nbsp;appke.</h1>
                    <ul className="promo-features">
                        <li><Clock size={20} /><span>QR dochádzka bez papierovačiek</span></li>
                        <li><Users size={20} /><span>Zamestnanci v systéme na pár klikov</span></li>
                        <li><ShieldCheck size={20} /><span>Bezpečné a v súlade s GDPR</span></li>
                    </ul>
                    <div className="promo-stores">
                        <a href="https://apps.apple.com/sk/app/effzone/id6747626666" aria-label="Download on the App Store">
                            <img src={AppStoreBadge} alt="Download on the App Store" className="promo-store-badge" />
                        </a>
                        <a href="https://play.google.com/store/apps/details?id=scan.sbs.app" aria-label="Get it on Google Play">
                            <img src={GooglePlayBadge} alt="Get it on Google Play" className="promo-store-badge" />
                        </a>
                    </div>
                </div>
            </div>
            <div className="auth-form-panel">
                <div className="login-card">
                    <a href="https://effzone.sk" className="back-to-website-link">
                        <ArrowLeft size={16} />
                        <span>Späť na web</span>
                    </a>
                    <div className="auth-form-header-mobile">
                        <img src={logo} alt="Effzone Logo" className="promo-logo" />
                        <div className="promo-logo-text">
                            <span className="promo-logo-title">Effzone</span>
                            <span className="promo-logo-subtitle">Dochádzka a komunikácia</span>
                        </div>
                    </div>
                    {phoneMode ? (
                        <PhoneLoginPanel onBack={() => setPhoneMode(false)} />
                    ) : (
                    <>
                    <h2 className="title">Prihláste sa do svojho účtu</h2>
                    <p className="subtitle">Vitajte späť! Zadajte svoje údaje.</p>

                    <form onSubmit={handleSubmit(handleLogin)}>
                        <div className="form-group">
                            <input
                                type="text"
                                className="form-control"
                                autoComplete="username"
                                {...register("username")}
                                placeholder="E-mail"
                            />
                            {errors.username && <p className="error-message">{errors.username?.message}</p>}
                        </div>

                        <div className="form-group">
                            <input
                                type="password"
                                className="form-control"
                                {...register("password")}
                                placeholder="Heslo"
                            />
                            {errors.password && <p className="error-message">{errors.password?.message}</p>}
                        </div>

                        <div className="options">
                            <div className="remember-me">
                                <input type="checkbox" id="rememberMe" {...register("rememberMe")} />
                                <label htmlFor="rememberMe">Zapamätať si ma</label>
                            </div>
                            <Link to="/forgot-password" >Zabudnuté heslo</Link>
                        </div>

                        <div className="form-group">
                            <button className="btn btn-primary btn-block" disabled={loading}>
                                {loading && (
                                    <span className="spinner-border spinner-border-sm"></span>
                                )}
                                <span>Prihlásiť sa</span>
                            </button>
                        </div>

                        {successMessage && (
                            <div className="form-group">
                                <div className="alert alert-success" role="alert">
                                    {successMessage}
                                </div>
                            </div>
                        )}

                        {message && (
                            <div className="form-group">
                                <div className="alert alert-danger" role="alert">
                                    {message}
                                    {showResend && (
                                        <button
                                            type="button"
                                            className="btn-link"
                                            onClick={handleResend}
                                            disabled={loading}
                                        >
                                            Resend verification email
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {resendStatus && (
                            <div className="form-group">
                                <div className="alert alert-info" role="alert">
                                    {resendStatus}
                                </div>
                            </div>
                        )}
                    </form>

                    <div className="or-divider">
                        <span className="line"></span>
                        <span className="or-text">alebo</span>
                        <span className="line"></span>
                    </div>

                    <div className="google-login-container">
                        <GoogleLogin
                            onSuccess={handleGoogleLoginSuccess}
                            onError={handleGoogleLoginError}
                            locale="sk"
                            useOneTap
                            theme="outline"
                            shape="rectangular"
                            width={340}
                            align="center"
                        />
                    </div>

                    <div className="auth-footer-link mt-3">
                        <button type="button" className="btn-link" onClick={() => setPhoneMode(true)}>
                            Prihlásiť sa telefónnym číslom
                        </button>
                    </div>

                    <div className="auth-footer-link mt-3">
                        Nemáte účet? <a href="https://my.effzone.com/start">Vyskúšajte zadarmo</a>
                    </div>
                    </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Login;




